import { useInfiniteQuery, useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type {
  AgentCommand,
  AgentCommandType,
  AiAgent,
  CursorPage,
  Detection,
  Endpoint,
  EndpointFilters,
  EndpointHeartbeat,
  Mcp,
  PaginatedResponse,
  Plugin,
  Skill,
} from '@/types/api';

const ACTIVITY_PAGE_SIZE = 50;

// ── Query key factory ──────────────────────────────────────────────────────
export const endpointKeys = {
  all: ['endpoints'] as const,
  list: (filters: EndpointFilters) => [...endpointKeys.all, 'list', filters] as const,
  detail: (id: string) => [...endpointKeys.all, 'detail', id] as const,
  // Deliberately NOT nested under `all` — the realtime socket hook invalidates
  // the `['endpoints']` prefix on every endpoint:status event, and mutation
  // success handlers below invalidate the same prefix. Pages that opt out of
  // live/auto refresh (via `{ realtime: false }`) use this key instead so
  // neither of those trigger a refetch — only an explicit `refetch()` call does.
  staticList: (filters: EndpointFilters) => ['endpoints-static', 'list', filters] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────

export function useEndpoints(filters: EndpointFilters = {}, options: { realtime?: boolean } = {}) {
  const { realtime = true } = options;
  return useQuery({
    queryKey: realtime ? endpointKeys.list(filters) : endpointKeys.staticList(filters),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<Endpoint>>(
        '/endpoints',
        { params: filters },
      );
      return data;
    },
    placeholderData: keepPreviousData,
    ...(!realtime && {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }),
  });
}

/** Bare count matching the same filters as useEndpoints — for KPI numbers that don't need row data. */
export function useEndpointsCount(filters: EndpointFilters = {}) {
  return useQuery({
    queryKey: [...endpointKeys.all, 'count', filters] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<{ total: number }>('/endpoints/count', { params: filters });
      return data;
    },
    staleTime: 30_000,
  });
}

/** Minimal (id, hostname, alias, status) projection for every endpoint — for cross-referencing large tables without a full-row fetch. */
export function useEndpointsLookup() {
  return useQuery({
    queryKey: [...endpointKeys.all, 'lookup'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<{ id: string; hostname: string; alias?: string; status: string }[]>('/endpoints/lookup');
      return data;
    },
    staleTime: 30_000,
  });
}

export function useEndpoint(id: string) {
  return useQuery({
    queryKey: endpointKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<Endpoint>(`/endpoints/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useQuarantineEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<Endpoint>(`/endpoints/${id}/quarantine`);
      return data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(endpointKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: endpointKeys.all });
    },
  });
}

export interface EndpointDetectionsParams {
  limit?: number;
  severity?: string;
}

export function useEndpointDetections(endpointId: string, params: EndpointDetectionsParams = {}) {
  return useQuery({
    queryKey: ['endpoints', endpointId, 'detections', params] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: import('@/types/api').Detection[]; total: number; criticalCount: number; highCount: number; limit: number }>(
        `/endpoints/${endpointId}/detections`,
        { params },
      );
      return data;
    },
    enabled: Boolean(endpointId),
    staleTime: 30_000,
  });
}

export function useReleaseEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<Endpoint>(`/endpoints/${id}/quarantine`);
      return data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(endpointKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: endpointKeys.all });
    },
  });
}

// ── Agent-protocol hooks ───────────────────────────────────────────────────

export function useEndpointHeartbeats(endpointId: string, limit = 50) {
  return useQuery({
    queryKey: ['endpoints', endpointId, 'heartbeats', limit] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: EndpointHeartbeat[]; total: number }>(
        `/endpoints/${endpointId}/heartbeats`,
        { params: { limit } },
      );
      return data;
    },
    enabled: Boolean(endpointId),
  });
}

export function useEndpointCommandsHistory(
  endpointId: string,
  options: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: ['endpoints', endpointId, 'commands-history'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: AgentCommand[] }>(
        `/endpoints/${endpointId}/commands-history`,
      );
      return data;
    },
    enabled: Boolean(endpointId),
    refetchInterval: options.refetchInterval ?? false,
  });
}

export interface QueueCommandDto {
  type: AgentCommandType;
  payload?: Record<string, unknown>;
}

export function useQueueCommand(endpointId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: QueueCommandDto) => {
      const { data } = await apiClient.post<{ command_id: string }>(
        `/endpoints/${endpointId}/queue-command`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ['endpoints', endpointId, 'commands-history'],
      }),
  });
}

export function useEndpointDpiEvents(endpointId: string, limit = 50) {
  return useQuery({
    queryKey: ['endpoints', endpointId, 'dpi-events', limit] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<import('@/types/api').DpiEvent[]>(
        `/endpoints/${endpointId}/dpi-events`,
        { params: { limit } },
      );
      return data;
    },
    enabled: Boolean(endpointId),
    staleTime: 30_000,
  });
}

export interface EndpointAiAgentsResult {
  data: import('@/types/api').AiAgent[];
  total: number;
}

export function useEndpointAiAgents(endpointId: string, limit = 20) {
  return useQuery({
    queryKey: ['endpoints', endpointId, 'ai-agents', limit] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<EndpointAiAgentsResult>(
        `/endpoints/${endpointId}/ai-agents`,
        { params: { limit } },
      );
      return data;
    },
    enabled: Boolean(endpointId),
    staleTime: 30_000,
  });
}

export interface EndpointMcpsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface EndpointMcpsResult {
  data: import('@/types/api').Mcp[];
  total: number;
  page: number;
  pageSize: number;
}

export function useEndpointMcps(endpointId: string, query: EndpointMcpsQuery = {}) {
  const { page = 1, pageSize = 20, search } = query;
  return useQuery({
    queryKey: ['endpoints', endpointId, 'mcps', page, pageSize, search] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<EndpointMcpsResult>(
        `/endpoints/${endpointId}/mcps`,
        { params: { page, pageSize, search: search || undefined } },
      );
      return data;
    },
    enabled: Boolean(endpointId),
    staleTime: 30_000,
  });
}

// ── Endpoint activity tabs (AI Agents / MCP Servers / Violations) — cursor-paginated ─
// `enabled` gates the network call to whichever tab is actually open; once a
// page has been fetched it stays in the react-query cache (staleTime: Infinity)
// so switching tabs back and forth never re-fetches — only "Load more" does.

export function useEndpointAiAgentsCursor(endpointId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['endpoints', endpointId, 'ai-agents-cursor'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<AiAgent>>(
        `/endpoints/${endpointId}/ai-agents/cursor`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(endpointId),
    staleTime: Infinity,
  });
}

export function useEndpointMcpsCursor(endpointId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['endpoints', endpointId, 'mcps-cursor'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<Mcp>>(
        `/endpoints/${endpointId}/mcps/cursor`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(endpointId),
    staleTime: Infinity,
  });
}

export function useEndpointDetectionsCursor(endpointId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['endpoints', endpointId, 'detections-cursor'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<Detection>>(
        `/endpoints/${endpointId}/detections/cursor`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(endpointId),
    staleTime: Infinity,
  });
}

export function useEndpointSkillsCursor(endpointId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['endpoints', endpointId, 'skills-cursor'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<Skill>>(
        `/endpoints/${endpointId}/skills/cursor`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(endpointId),
    staleTime: Infinity,
  });
}

export function useEndpointPluginsCursor(endpointId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['endpoints', endpointId, 'plugins-cursor'] as const,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<CursorPage<Plugin>>(
        `/endpoints/${endpointId}/plugins/cursor`,
        { params: { cursor: pageParam, limit: ACTIVITY_PAGE_SIZE } },
      );
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && Boolean(endpointId),
    staleTime: Infinity,
  });
}

export function useSetEndpointMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, mode }: { id: string; mode: 'active' | 'passive' | 'off' }) => {
      const { data } = await apiClient.patch<Endpoint>(`/endpoints/${id}/mode`, { mode });
      return data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(endpointKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: endpointKeys.all });
    },
  });
}

export function useBulkSetEndpointMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, mode }: { ids: string[]; mode: 'active' | 'passive' | 'off' }) => {
      const { data } = await apiClient.post<{ updated: number }>('/endpoints/bulk-mode', { ids, mode });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: endpointKeys.all });
    },
  });
}

export function useDeleteEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/endpoints/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: endpointKeys.all });
    },
  });
}

export function useSetEndpointAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, alias }: { id: string; alias: string }) => {
      const { data } = await apiClient.patch<Endpoint>(`/endpoints/${id}/alias`, { alias });
      return data;
    },
    onSuccess: (updated) => {
      qc.setQueryData(endpointKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: endpointKeys.all });
    },
  });
}
